/**
 * Access tokens live in memory + sessionStorage (not localStorage).
 * localStorage survives XSS and browser restarts; sessionStorage is per-tab
 * and is cleared when the tab closes. httpOnly cookies remain a later auth wave.
 */

const ACCESS_KEY = 'argislegal_access_token';
const REFRESH_KEY = 'argislegal_refresh_token';
const PORTAL_KEY = 'argislegal_portal_token';
const LEGACY_ACCESS = 'token';
const LEGACY_REFRESH = 'refreshToken';
const LEGACY_PORTAL = 'portalToken';

let memoryAccess: string | null = null;
let memoryPortal: string | null = null;

function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string | null) {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

function clearLegacy(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function takeLegacy(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    if (value) localStorage.removeItem(key);
    return value;
  } catch {
    return null;
  }
}

export function getAuthToken(): string | null {
  if (memoryAccess) return memoryAccess;
  const fromSession = readSession(ACCESS_KEY);
  if (fromSession) {
    memoryAccess = fromSession;
    return fromSession;
  }
  const legacy = takeLegacy(LEGACY_ACCESS);
  if (legacy) {
    setAuthToken(legacy);
    return legacy;
  }
  return null;
}

export function setAuthToken(token: string | null) {
  memoryAccess = token;
  writeSession(ACCESS_KEY, token);
  clearLegacy(LEGACY_ACCESS);
}

export function getRefreshToken(): string | null {
  return readSession(REFRESH_KEY) || takeLegacy(LEGACY_REFRESH);
}

export function setRefreshToken(token: string | null) {
  writeSession(REFRESH_KEY, token);
  clearLegacy(LEGACY_REFRESH);
}

export function getPortalToken(): string | null {
  if (memoryPortal) return memoryPortal;
  const fromSession = readSession(PORTAL_KEY);
  if (fromSession) {
    memoryPortal = fromSession;
    return fromSession;
  }
  const legacy = takeLegacy(LEGACY_PORTAL);
  if (legacy) {
    setPortalToken(legacy);
    return legacy;
  }
  return null;
}

export function setPortalToken(token: string | null) {
  memoryPortal = token;
  writeSession(PORTAL_KEY, token);
  clearLegacy(LEGACY_PORTAL);
}

export function clearAuthTokens() {
  memoryAccess = null;
  writeSession(ACCESS_KEY, null);
  writeSession(REFRESH_KEY, null);
  clearLegacy(LEGACY_ACCESS);
  clearLegacy(LEGACY_REFRESH);
}

export function clearPortalToken() {
  memoryPortal = null;
  writeSession(PORTAL_KEY, null);
  clearLegacy(LEGACY_PORTAL);
}
